import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { DialogConfig, DialogService } from '@ddt-web/dialog';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * Dialog component specific to 360 Evaluator - Scenario Analysis used to customize the dollar amount for the selected scenarios.
 *
 * @export
 * @class CustomizeDollarAmountDialogComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@UntilDestroy()
@Component({
	selector: 'sc-customize-dollar-amount-dialog',
	templateUrl: './customize-dollar-amount-dialog.component.html',
	styleUrls: ['./customize-dollar-amount-dialog.component.scss']
})
export class CustomizeDollarAmountDialogComponent implements OnInit {

	public userInputAum: string = '';
	public isAumValid: boolean = false;
	public isAumEmpty: boolean = true;

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		private storeService: StoreService,
		private config: DialogConfig,
		private dialogService: DialogService,
		private contentService: ContentService,
		private tealiumAnalyticsService: TealiumAnalyticsService) {
	}

	@HostListener('document:keydown.escape', ['$event']) public onKeydownHandler(event: KeyboardEvent): void {
		this.dialogService.close();
	}

	public ngOnInit(): void {
		this.initializeDefaultAum();
		this.validateAum();
	}

	/**
	 * Updates and validates the aum user input.
	 *
	 * @param {string} value
	 */
	public onAumValueChange(value): void {
		this.userInputAum = value.replace(/\D/g, '');
		this.validateAum();
	}

	/**
	 * Updates the aumValueConfig feature store with the current userInputAum.
	 * Once the value is updated, the dialog is closed.
	*/
	public onSaveClick(): void {
		this.storeService.loadOne(this.storeConfig.aumValueConfig, Number(this.userInputAum));
		this.dialogService.close();
		this.sendTealiumEvent('save', this.userInputAum);
	}

	/**
	 * Closes the dialog.
	*/
	public onCloseClick(): void {
		this.dialogService.close();
		this.sendTealiumEvent('close', this.userInputAum);
	}

	/**
	 * Updates the isAumValid and isAumEmpty for the current userInputAum.
	 *
	 * @private
	*/
	private validateAum(): void {
		this.isAumValid = !isNaN(Number(this.userInputAum)) && Number(this.userInputAum) > 0;
		this.isAumEmpty = this.userInputAum === '';
	}

	/**
	 * Sets the default userInputAum with the current baseline value from the aumValueConfig feature store.
	 *
	 * @private
	*/
	private initializeDefaultAum(): void {
		this.storeService.selectAll(this.storeConfig.aumValueConfig)
			.pipe(untilDestroyed(this))
			.subscribe((defaultAum: any) => {
				this.userInputAum = defaultAum;
			});
	}

	/**
	 * Sends tealium event for save/cancel.
	 *
	 * @private
	*/
	private sendTealiumEvent(action: string, aum: string): void {
		const tealiumEventName = `scenarios:customize-dollar-amount:${action}:${aum}`;
		const tealiumEvent = new TealiumAnalyticsEvent(tealiumEventName, {});
		this.tealiumAnalyticsService.trackEventTealium(tealiumEvent);
	}

}
